/**
 * Marketplace React-Query hooks (Macro #29 Stage 3).
 * All data is backend-authoritative; zone comes from the canonical SaleV2
 * quote (`saleApi.quote`), reused verbatim (no new zone truth invented).
 */
import { useQueries, useQuery } from "@tanstack/react-query";
import { marketplaceApi, type Listing } from "../../lib/marketplace-api";
import { saleApi } from "../../lib/sale-api";
import type { GridSelection } from "../../lib/grid-selection";
import { bboxFromCells, zoneKey, type ZoneKey } from "./mkt-format";

export function useMarketplaceConfig() {
  return useQuery({
    queryKey: ["mkt", "config"],
    queryFn: ({ signal }) => marketplaceApi.config(signal),
    staleTime: 60_000,
  });
}

export function useListings(status = "active") {
  return useQuery({
    queryKey: ["mkt", "listings", status],
    queryFn: ({ signal }) => marketplaceApi.listings({ status, limit: 100 }, signal),
    staleTime: 10_000,
  });
}

export function useListing(orderHash: string | null) {
  return useQuery({
    queryKey: ["mkt", "listing", orderHash],
    queryFn: ({ signal }) => marketplaceApi.listing(orderHash as string, signal),
    enabled: !!orderHash,
    staleTime: 10_000,
  });
}

export function useAssetOffers(assetKey: string | null) {
  return useQuery({
    queryKey: ["mkt", "offers", assetKey],
    queryFn: ({ signal }) => marketplaceApi.assetOffers(assetKey as string, signal),
    enabled: !!assetKey,
    staleTime: 10_000,
  });
}

export function useArtworks() {
  return useQuery({
    queryKey: ["mkt", "artworks"],
    queryFn: ({ signal }) => marketplaceApi.artworks(signal),
    staleTime: 10_000,
  });
}

export function useArtwork(artworkId: string | null) {
  return useQuery({
    queryKey: ["mkt", "artwork", artworkId],
    queryFn: ({ signal }) => marketplaceApi.artwork(artworkId as string, signal),
    enabled: !!artworkId,
    retry: false,
    staleTime: 10_000,
  });
}

export function useAuctions() {
  return useQuery({
    queryKey: ["mkt", "auctions"],
    queryFn: ({ signal }) => marketplaceApi.auctions(signal),
    staleTime: 5_000,
    refetchInterval: 15_000,
  });
}

export function useMine(enabled: boolean) {
  return useQuery({
    queryKey: ["mkt", "mine"],
    queryFn: ({ signal }) => marketplaceApi.mine(signal),
    enabled,
    retry: false,
    staleTime: 5_000,
  });
}

/** Ordered ownership/provenance timeline for a LAND asset (canonical, real-only). */
export function useLandProvenance(assetKey: string | null) {
  return useQuery({
    queryKey: ["mkt", "provenance", assetKey],
    queryFn: ({ signal }) => marketplaceApi.landProvenance(assetKey as string, signal),
    enabled: !!assetKey,
    retry: false,
    staleTime: 10_000,
  });
}

export function useOwnerAudit(enabled: boolean) {
  return useQuery({
    queryKey: ["mkt", "owner", "audit"],
    queryFn: ({ signal }) => marketplaceApi.ownerAudit(signal),
    enabled,
    retry: false,
    staleTime: 5_000,
  });
}

export function useOwnerCapabilities(enabled: boolean) {
  return useQuery({
    queryKey: ["mkt", "owner", "capabilities"],
    queryFn: ({ signal }) => marketplaceApi.ownerCapabilities(signal),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export function useOwnerSupplyCheck(owner: string | null) {
  return useQuery({
    queryKey: ["mkt", "owner", "supply-check", owner],
    queryFn: ({ signal }) => marketplaceApi.ownerSupplyCheck(owner as string, signal),
    enabled: !!owner,
    retry: false,
    staleTime: 5_000,
  });
}

/** Authoritative zone classification for a listed parcel (SaleV2 quote). */
export function useListingZone(cells: [number, number][] | undefined): ZoneKey {
  const bbox = cells && cells.length ? bboxFromCells(cells) : null;
  const key = bbox ? `${bbox.x},${bbox.y},${bbox.w},${bbox.h}` : "none";
  const q = useQuery({
    queryKey: ["mkt", "zone", key],
    enabled: !!bbox,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const sel = { x: bbox!.x, y: bbox!.y, w: bbox!.w, h: bbox!.h } as unknown as GridSelection;
      const quote = await saleApi.quote(sel);
      return quote.generation === "v2" ? quote.zone : "";
    },
    retry: false,
  });
  return zoneKey(q.data ?? undefined);
}

/** Derive the zone for a listing from its cells (helper for cards/filters). */
export function listingCells(l: Listing): [number, number][] {
  return (l.cells ?? []).map((c) => [Number(c[0]), Number(c[1])] as [number, number]);
}

/** Resolve zones for many listings in parallel → Map<order_hash, ZoneKey>. */
export function useListingsZones(listings: Listing[]): Map<string, ZoneKey> {
  const results = useQueries({
    queries: listings.map((l) => {
      const cells = listingCells(l);
      const bbox = cells.length ? bboxFromCells(cells) : null;
      const key = bbox ? `${bbox.x},${bbox.y},${bbox.w},${bbox.h}` : "none";
      return {
        queryKey: ["mkt", "zone", key],
        enabled: !!bbox,
        staleTime: 5 * 60_000,
        retry: false,
        queryFn: async () => {
          const sel = { x: bbox!.x, y: bbox!.y, w: bbox!.w, h: bbox!.h } as unknown as GridSelection;
          const quote = await saleApi.quote(sel);
          return quote.generation === "v2" ? quote.zone : "";
        },
      };
    }),
  });
  const map = new Map<string, ZoneKey>();
  listings.forEach((l, i) => {
    map.set(l.order_hash, zoneKey(results[i]?.data ?? undefined));
  });
  return map;
}
